import { useQuery } from "@apollo/client";
import { FACILITIES_QUERY } from "components/FacilityPicker";
import { TENANTS_QUERY } from "components/MultipleTenantPicker";
import { useMemo } from "react";

/** Filter types whose values are facility ids rather than readable text. */
const FACILITY_FILTER_KEY_TYPES = new Set([
  "facility",
  "multipleFacility",
  "wasteFacility",
  "wasteMultipleFacility",
]);

/** Filter types whose values are tenant ids rather than readable text. */
const TENANT_FILTER_KEY_TYPES = new Set(["multipleTenant"]);

export function isFacilityFilterKeyType(filterKeyType: string | undefined): boolean {
  return filterKeyType !== undefined && FACILITY_FILTER_KEY_TYPES.has(filterKeyType);
}

export function isTenantFilterKeyType(filterKeyType: string | undefined): boolean {
  return filterKeyType !== undefined && TENANT_FILTER_KEY_TYPES.has(filterKeyType);
}

const EMPTY_NAMES: ReadonlyMap<string, string> = new Map();

/**
 * The lookup a picker query needs to resolve a pill's ids to names.
 *
 * One query covers every id on the row — `ids:` takes a comma-separated list
 * and skips anything that isn't a valid id. Ids are sorted and de-duplicated so
 * a reordered pill row doesn't look like a new query to Apollo.
 */
function idsQueryOptions(ids: string[]) {
  const idList = Array.from(new Set(ids)).sort().join(",");
  return {
    variables: {
      search: `ids:${idList}`,
      page: 0,
      pageSize: idList.length > 0 ? idList.split(",").length : 1,
    },
    skip: idList.length === 0,
    fetchPolicy: "cache-first" as const,
  };
}

/**
 * Ids with no match are simply absent from the map; callers fall back to
 * showing the id, which is also what happens while the query is in flight.
 */
function namesById(
  items: readonly ({ id: string; name?: string | null } | null | undefined)[] | undefined | null
): ReadonlyMap<string, string> {
  if (!items || items.length === 0) {
    return EMPTY_NAMES;
  }
  return new Map(items.flatMap((item) => (item?.name ? [[item.id, item.name] as const] : [])));
}

function useFacilityPillNames(facilityIds: string[]): ReadonlyMap<string, string> {
  const { data } = useQuery(FACILITIES_QUERY, idsQueryOptions(facilityIds));
  return useMemo(() => namesById(data?.facilities?.items), [data]);
}

function useTenantPillNames(tenantIds: string[]): ReadonlyMap<string, string> {
  const { data } = useQuery(TENANTS_QUERY, idsQueryOptions(tenantIds));
  return useMemo(() => namesById(data?.tenants?.items), [data]);
}

/**
 * Resolves the pill values that are ids to the names a user recognizes.
 *
 * The omnisearch string carries `facilityId:<uuid>` and `tenantId:<uuid>`
 * because that is what the backend filters on, so those pills would otherwise
 * read as raw guids. Facility and tenant ids can't collide, so one map keyed by
 * id serves both.
 */
export function usePillValueNames(
  facilityIds: string[],
  tenantIds: string[]
): ReadonlyMap<string, string> {
  const facilityNames = useFacilityPillNames(facilityIds);
  const tenantNames = useTenantPillNames(tenantIds);

  return useMemo(() => {
    if (tenantNames.size === 0) {
      return facilityNames;
    }
    if (facilityNames.size === 0) {
      return tenantNames;
    }
    return new Map([...facilityNames, ...tenantNames]);
  }, [facilityNames, tenantNames]);
}
